import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import WhatsAppCTA from "../components/WhatsAppCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const demoPost = {
  id: "1",
  title: "Cómo elegir la vegetación correcta para el clima de Los Cabos",
  slug: "vegetacion-correcta-los-cabos",
  excerpt: "Guía técnica para seleccionar plantas que prosperan en el microclima único del desierto tropical.",
  content: `
## La importancia de entender el microclima

Los Cabos presenta un ecosistema único: un desierto tropical donde convergen condiciones extremas. La selección vegetal debe considerar múltiples factores que van más allá de la estética.

### Factores clave a considerar

1. **Exposición solar**: La intensidad del sol en Baja California Sur es significativamente mayor que en otras regiones. Las plantas seleccionadas deben tolerar radiación UV intensa.

2. **Suelo y drenaje**: El suelo desértico tiene características particulares de retención de agua y nutrientes que influyen directamente en la supervivencia de las especies.

3. **Vientos**: Los vientos del Mar de Cortés y del Pacífico afectan tanto la evaporación como la estructura de las plantas.

## Especies recomendadas

### Para zonas de alto impacto solar

- **Agave attenuata**: Resistente y escultural, ideal para puntos focales.
- **Pachypodium lamerei**: Aporta verticalidad sin requerir riego constante.
- **Euphorbia trigona**: Textura interesante con mínimo mantenimiento.

### Para áreas de sombra parcial

- **Philodendron selloum**: Follaje tropical que prospera con luz filtrada.
- **Strelitzia reginae**: Color y estructura en espacios protegidos.

## El error más común

Importar vegetación de climas húmedos sin considerar la adaptación. Un jardín que requiere riego excesivo no solo es insostenible económicamente, sino que atenta contra el equilibrio del ecosistema local.

> "El paisaje no debe luchar contra el clima, debe fluir con él."

## Conclusión

La selección vegetal correcta es la base de un paisaje que evoluciona positivamente con el tiempo. Invertir en un estudio técnico inicial evita costosos errores y garantiza la viabilidad a largo plazo de tu inversión.
  `,
  cover_image: "/assets/projects/jardin-nacho/03_dsc00509.jpg",
  tags: ["Vegetación", "Diseño", "Los Cabos"],
  published: true,
  created_at: "2026-01-15T10:00:00Z"
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API}/blog/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
      } else {
        setPost(demoPost);
      }
    } catch (error) {
      console.error("Error fetching post:", error);
      setPost(demoPost);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  // Simple markdown parser
  const parseMarkdown = (content) => {
    if (!content) return "";
    
    return content
      .split("\n")
      .map((line, i) => {
        // Headers
        if (line.startsWith("### ")) {
          return `<h3 class="text-xl font-serif mt-8 mb-4">${line.slice(4)}</h3>`;
        }
        if (line.startsWith("## ")) {
          return `<h2 class="text-2xl font-serif mt-12 mb-6">${line.slice(3)}</h2>`;
        }
        
        // Blockquotes
        if (line.startsWith("> ")) {
          return `<blockquote class="border-l-2 border-circulo-sand pl-6 my-8 text-lg italic text-muted-foreground">${line.slice(2)}</blockquote>`;
        }
        
        // Lists
        if (line.startsWith("- **")) {
          const match = line.match(/- \*\*(.+?)\*\*: (.+)/);
          if (match) {
            return `<li class="mb-2"><strong class="text-white">${match[1]}</strong>: ${match[2]}</li>`;
          }
        }
        if (line.startsWith("- ")) {
          return `<li class="mb-2">${line.slice(2)}</li>`;
        }
        if (line.match(/^\d+\. \*\*/)) {
          const match = line.match(/^\d+\. \*\*(.+?)\*\*: (.+)/);
          if (match) {
            return `<li class="mb-3"><strong class="text-white">${match[1]}</strong>: ${match[2]}</li>`;
          }
        }
        
        // Bold
        let processed = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>');
        
        // Paragraphs
        if (processed.trim() && !processed.startsWith("<")) {
          return `<p class="mb-4 text-muted-foreground leading-relaxed">${processed}</p>`;
        }
        
        return processed;
      })
      .join("");
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="animate-pulse">
            <div className="h-8 bg-circulo-subtle w-32 mb-8" />
            <div className="h-12 bg-circulo-subtle w-3/4 mb-4" />
            <div className="h-6 bg-circulo-subtle w-1/2 mb-8" />
            <div className="aspect-video bg-circulo-subtle" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Artículo no encontrado</h1>
          <Link to="/blog" className="btn-secondary">
            Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="blog-post-page">
      <article className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft size={16} />
            Volver al blog
          </Link>
        </motion.div>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((tag) => (
              <span key={tag} className="tag">{tag}</span>
            ))}
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-medium mb-6 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              {formatDate(post.created_at)}
            </div>
          </div>
        </motion.header>

        {/* Cover Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="aspect-video overflow-hidden">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        {/* Excerpt */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-xl text-muted-foreground leading-relaxed mb-12 pb-12 border-b border-white/10"
        >
          {post.excerpt}
        </motion.p>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="prose prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: parseMarkdown(post.content) }}
        />

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <WhatsAppCTA
            source={`blogpost_${post?.slug}`}
            title="¿Listo para transformar tu espacio?"
            subtitle="Comenzamos con un estudio técnico del sitio para diseñar el paisaje perfecto para tu propiedad."
            buttonText="Agendar por WhatsApp"
            message="Hola, leí su artículo y me interesa agendar un estudio de suelo."
            variant="dark"
          />
        </motion.div>
      </article>
    </div>
  );
}
